/**
 * Author: Claude Code
 * Generation: Room management store for lobby system
 * Version: v3.2.127
 * Modified: 2025-10-15 (Added persist middleware for state recovery on page refresh)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export interface GameRoom {
  id: string;
  name: string;
  participantCount: number;
  maxParticipants: number;
  spectatorCount: number; // P2.1: Spectator count for real-time display
  status: 'WAITING' | 'CONFIGURING' | 'ACTIVE' | 'FINISHED';
  createdBy: string;
  createdAt: string;
  participants: RoomParticipant[];
}

export interface RoomParticipant {
  id: string;
  username: string;
  isReady: boolean;
  agentConfiguration?: AgentConfiguration;
}

export interface Slime {
  emoji: string;
  name: string;
}

export interface AgentConfiguration {
  // Core attributes
  attack: number;
  speed: number;
  defense: number;
  hp: number;
  style: number; // 0-10 fighting style (aggressiveness)

  // Agent identity (v3.2.41)
  name: string;
  slimeType: string; // emoji
  slimeName: string;
  personality: string;
  strategy: string;

  // Optional per-agent LLM model selection (sent to backend)
  modelId?: string;
}

interface RoomState {
  // Room list management
  rooms: GameRoom[];
  roomsLoading: boolean;
  roomsError: string | null;

  // Current room state
  currentRoom: GameRoom | null;
  isInRoom: boolean;
  isSpectating: boolean; // P2.1: Spectator mode flag

  // User's agent configuration
  myAgentConfiguration: AgentConfiguration | null;
  isConfigured: boolean;
  // LLM model selection (frontend-only, submit with agent if needed)
  selectedModelId: string; // e.g., 'doubao-seed-1-6-251015'

  // Room creation
  createRoomLoading: boolean;
  createRoomError: string | null;

  // Agent creation flow (v3.2.41)
  agentCreationStep: number; // 1-3
  agentCreationTimeRemaining: number; // seconds
  currentSlimeIndex: number;
  availableSlimes: Slime[];

  // WaitingRoom control (v3.2.41)
  showWaitingRoom: boolean;

  // P1.8: Game state management for E2E flow
  gameStarted: boolean;
  gameId: string | null;
  // Solo mode flag
  singlePlayerMode: boolean;
}

interface RoomActions {
  // Room list actions
  loadRooms: () => Promise<void>;
  refreshRooms: () => Promise<void>;
  setRoomsLoading: (loading: boolean) => void;
  setRoomsError: (error: string | null) => void;

  // Room management actions
  createRoom: (name: string, maxParticipants: number) => Promise<GameRoom | null>;
  joinRoom: (roomId: string) => Promise<boolean>;
  joinRoomAsSpectator: (roomId: string) => Promise<boolean>; // P2.1: Join as spectator
  leaveRoom: () => Promise<void>;

  // Agent configuration actions
  updateAgentConfiguration: (config: AgentConfiguration) => void;
  submitAgentConfiguration: () => Promise<boolean>;
  setReady: (ready: boolean) => Promise<void>;

  // Agent creation flow control (v3.2.41)
  setAgentCreationStep: (step: number) => void;
  nextAgentCreationStep: () => void;
  previousAgentCreationStep: () => void;

  // Slime selection (v3.2.41)
  setSlimeIndex: (index: number) => void;
  nextSlime: () => void;
  previousSlime: () => void;

  // Agent data updates (v3.2.41)
  updateAgentName: (name: string) => void;
  updateAgentPersonality: (text: string) => void;
  updateAgentStrategy: (text: string) => void;
  // LLM model selection
  updateSelectedModel: (modelId: string) => void;

  // Timer management (v3.2.41)
  setAgentCreationTime: (seconds: number) => void;
  startAgentCreationTimer: () => void;
  stopAgentCreationTimer: () => void;

  // Agent data retrieval (v3.2.41)
  getCompleteAgentData: () => AgentConfiguration | null;
  getRemainingAttributePoints: () => number;

  // Reset (v3.2.41)
  resetAgentCreation: () => void;

  // WaitingRoom control (v3.2.41)
  openWaitingRoom: () => void;
  closeWaitingRoom: () => void;
  toggleWaitingRoom: (show: boolean) => void;

  // WebSocket event handlers
  handleRoomCreated: (room: GameRoom) => void;
  handleRoomUpdated: (room: GameRoom) => void;
  handleRoomDeleted: (roomId: string) => void;
  handleUserJoined: (roomId: string, participant: RoomParticipant) => void;
  handleUserLeft: (roomId: string, userId: string) => void;
  handleGameStarted: (data: { gameId: string; roomId: string }) => void; // P1.8: E2E game started event
  handleUserConfigured: (roomId: string, participant: RoomParticipant) => void;
  handleUserReady: (roomId: string, participant: RoomParticipant) => void;
  handleRoomStateSynced: (data: { rooms: GameRoom[]; participants: RoomParticipant[] }) => void; // v3.2.139: Room state sync

  // P2.1: Spectator event handlers
  handleSpectatorJoined: (data: { roomId: string; spectatorCount: number }) => void;
  handleSpectatorLeft: (data: { roomId: string; spectatorCount: number }) => void;

  // Reset and cleanup
  resetRoomState: () => void;
  clearErrors: () => void;
  // Solo mode
  setSinglePlayerMode: (flag: boolean) => void;
}

// Available slimes for selection（统一为7色，与素材/动画系统一致）
const AVAILABLE_SLIMES: Slime[] = [
  { emoji: '🟢', name: 'Green Warrior' },
  { emoji: '🔴', name: 'Red Berserker' },
  { emoji: '🟣', name: 'Purple Assassin' },
  { emoji: '🔵', name: 'Blue Mage' },
  { emoji: '⚪', name: 'White Monk' },
  { emoji: '⚫', name: 'Black Rogue' },
  { emoji: '🟠', name: 'Orange Tank' }
];

const initialAgentConfig: AgentConfiguration = {
  // Core attributes (100 points total)
  attack: 25,
  speed: 10,
  defense: 20,
  hp: 30,
  style: 7, // 0-10 aggressiveness

  // Agent identity
  name: 'Thunder Knight',
  slimeType: '🟢',
  slimeName: 'Green Warrior',
  personality: 'Brave and strategic fighter who values honor above all.',
  strategy: '• Prioritize weapon collection early\n• Stay near safe zone edges\n• Engage only with advantage\n• Retreat if health < 30%'
};

export const useRoomStore = create<RoomState & RoomActions>()(
  persist(
    immer((set, get) => ({
    // Initial state
    rooms: [],
    roomsLoading: false,
    roomsError: null,
    currentRoom: null,
    isInRoom: false,
    isSpectating: false, // P2.1: Spectator mode initial state
    myAgentConfiguration: null,
    isConfigured: false,
    selectedModelId: 'doubao-seed-1-6-251015',
    createRoomLoading: false,
    createRoomError: null,

    // Agent creation flow
    agentCreationStep: 1,
    agentCreationTimeRemaining: 60,
    currentSlimeIndex: 0,
    availableSlimes: AVAILABLE_SLIMES,

    // WaitingRoom control
    showWaitingRoom: false,

    // P1.8: Game state
    gameStarted: false,
    gameId: null,
    singlePlayerMode: false,

    // Room list actions
    loadRooms: async () => {
      set((state) => {
        state.roomsLoading = true;
        state.roomsError = null;
      });

      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        const response = await webSocketService.emit('rooms:list');

        if (response && response.success) {
          set((state) => {
            state.rooms = response.rooms as GameRoom[];
            state.roomsLoading = false;
          });
        } else {
          throw new Error(response?.error || 'Failed to load rooms');
        }
      } catch (error) {
        set((state) => {
          state.roomsError = error instanceof Error ? error.message : 'Failed to load rooms';
          state.roomsLoading = false;
        });
      }
    },

    refreshRooms: async () => {
      await get().loadRooms();
    },

    setRoomsLoading: (loading: boolean) => {
      set((state) => {
        state.roomsLoading = loading;
      });
    },

    setRoomsError: (error: string | null) => {
      set((state) => {
        state.roomsError = error;
      });
    },

    // Room management actions
    createRoom: async (name: string, maxParticipants: number) => {
      set((state) => {
        state.createRoomLoading = true;
        state.createRoomError = null;
      });

      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        const response = await webSocketService.emit('rooms:create', { name, maxParticipants });

        if (response && response.success && response.room) {
          set((state) => {
            state.rooms.push(response.room);
            state.createRoomLoading = false;
            // ✅ Bug Fix: Ensure participants array is properly initialized
            state.currentRoom = {
              ...response.room,
              participants: response.room.participants || [] // Defensive initialization
            };
            state.isInRoom = true;
            state.myAgentConfiguration = { ...initialAgentConfig };
          });
          return response.room;
        } else {
          throw new Error(response?.error || 'Failed to create room');
        }
      } catch (error) {
        set((state) => {
          state.createRoomError = error instanceof Error ? error.message : 'Failed to create room';
          state.createRoomLoading = false;
        });
        return null;
      }
    },

    joinRoom: async (roomId: string) => {
      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        const response = await webSocketService.emit('rooms:join', { roomId });

        if (response && response.success && response.room) {
          set((state) => {
            // ✅ Bug Fix: Ensure participants array is properly initialized
            state.currentRoom = {
              ...response.room,
              participants: response.room.participants || [] // Defensive initialization
            };
            state.isInRoom = true;
            state.myAgentConfiguration = { ...initialAgentConfig };
          });
          return true;
        } else {
          console.error('Failed to join room:', response?.error || 'Unknown error');
          return false;
        }
      } catch (error) {
        console.error('Failed to join room:', error);
        return false;
      }
    },

    // 👁️ P2.1: Join room as spectator（必须登录）
    joinRoomAsSpectator: async (roomId: string) => {
      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        const { useAuthStore } = await import('../store/AuthStore');
        const { isAuthenticated, token, openAuthModal } = useAuthStore.getState() as any;

        // 登录门禁：未登录则弹出登录框并阻止继续
        if (!isAuthenticated || !token) {
          console.warn('⚠️ Spectate requires authentication. Opening sign-in modal...');
          openAuthModal?.('signin');
          return false;
        }

        const response = await webSocketService.emit('spectator:join', { roomId });
        if (response && response.success) {
          set((state) => {
            state.currentRoom = response.room || { id: roomId } as GameRoom;
            state.isInRoom = true;
            state.isSpectating = true;
            state.myAgentConfiguration = null;
          });
          return true;
        } else {
          console.error('Failed to join as spectator:', response?.error || 'Unknown error');
          return false;
        }
      } catch (error) {
        console.error('Failed to join as spectator:', error);
        return false;
      }
    },

    leaveRoom: async () => {
      const currentRoom = get().currentRoom;
      const isSpectating = get().isSpectating;
      if (!currentRoom) return;

      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;

        // 👁️ P2.1: Use different events for spectators vs participants
        if (isSpectating) {
          await webSocketService.emit('spectator:leave', { roomId: currentRoom.id });
        } else {
          await webSocketService.emit('rooms:leave', { roomId: currentRoom.id });
        }

        set((state) => {
          state.currentRoom = null;
          state.isInRoom = false;
          state.isSpectating = false; // P2.1: Reset spectator flag
          state.myAgentConfiguration = null;
          state.isConfigured = false;
        });
      } catch (error) {
        console.error('Failed to leave room:', error);
      }
    },

    // Agent configuration actions
    updateAgentConfiguration: (config: AgentConfiguration) => {
      // v3.2.50: Allow partial allocation during editing (total ≤ 100)
      // Strict validation (total = 100) happens at submission in AgentCreationPage
      const totalPoints = config.attack + config.speed + config.defense + config.hp;
      if (totalPoints > 100) {
        console.warn('Invalid configuration: total points cannot exceed 100, got', totalPoints);
        return;
      }

      set((state) => {
        state.myAgentConfiguration = config;
      });
    },

    submitAgentConfiguration: async () => {
      const config = get().myAgentConfiguration;
      const room = get().currentRoom;
      const selectedModelId = get().selectedModelId;
      
      if (!config || !room) return false;

      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        // 将前端选择的模型并入配置，后端在房间初始化时按 agent 绑定模型
        const payloadCfg = { ...config, modelId: selectedModelId } as any;
        const response = await webSocketService.emit('rooms:configure', { roomId: room.id, configuration: payloadCfg });

        if (response && response.success) {
          set((state) => {
            state.isConfigured = true;
          });
          return true;
        } else {
          console.error('Failed to submit configuration:', response?.error || 'Unknown error');
          return false;
        }
      } catch (error) {
        console.error('Failed to submit configuration:', error);
        return false;
      }
    },

    setReady: async (ready: boolean) => {
      const room = get().currentRoom;
      if (!room) return;

      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        await webSocketService.emit('rooms:ready', { roomId: room.id, ready });

        // Ready state will be updated via WebSocket events
      } catch (error) {
        console.error('Failed to set ready state:', error);
      }
    },

    // WebSocket event handlers
    handleRoomCreated: (room: GameRoom) => {
      set((state) => {
        const existingIndex = state.rooms.findIndex(r => r.id === room.id);
        if (existingIndex === -1) {
          state.rooms.push(room);
        }
      });
    },

    handleRoomUpdated: (room: GameRoom) => {
      set((state) => {
        const index = state.rooms.findIndex(r => r.id === room.id);
        if (index !== -1) {
          state.rooms[index] = room;
        }
        
        // Update current room if it's the same
        if (state.currentRoom && state.currentRoom.id === room.id) {
          state.currentRoom = room;
        }
      });
    },

    handleRoomDeleted: (roomId: string) => {
      set((state) => {
        state.rooms = state.rooms.filter(room => room.id !== roomId);
        
        // Leave room if we were in it
        if (state.currentRoom && state.currentRoom.id === roomId) {
          state.currentRoom = null;
          state.isInRoom = false;
          state.myAgentConfiguration = null;
          state.isConfigured = false;
        }
      });
    },

    handleUserJoined: (roomId: string, participant: RoomParticipant) => {
      set((state) => {
        const room = state.rooms.find(r => r.id === roomId);
        if (room) {
          room.participants.push(participant);
          room.participantCount = room.participants.length;
        }
        
        if (state.currentRoom && state.currentRoom.id === roomId) {
          state.currentRoom.participants.push(participant);
          state.currentRoom.participantCount = state.currentRoom.participants.length;
        }
      });
    },

    handleUserLeft: (roomId: string, userId: string) => {
      set((state) => {
        const room = state.rooms.find(r => r.id === roomId);
        if (room) {
          room.participants = room.participants.filter(p => p.id !== userId);
          room.participantCount = room.participants.length;
        }
        
        if (state.currentRoom && state.currentRoom.id === roomId) {
          state.currentRoom.participants = state.currentRoom.participants.filter(p => p.id !== userId);
          state.currentRoom.participantCount = state.currentRoom.participants.length;
        }
      });
    },

    handleUserConfigured: (roomId: string, participant: RoomParticipant) => {
      set((state) => {
        const updateParticipant = (participants: RoomParticipant[]) => {
          const existingParticipant = participants.find(p => p.id === participant.id);
          if (existingParticipant) {
            existingParticipant.agentConfiguration = participant.agentConfiguration;
            existingParticipant.isReady = participant.isReady;
          }
        };

        const room = state.rooms.find(r => r.id === roomId);
        if (room) {
          updateParticipant(room.participants);
        }

        if (state.currentRoom && state.currentRoom.id === roomId) {
          updateParticipant(state.currentRoom.participants);
        }
      });
    },

    handleUserReady: (roomId: string, participant: RoomParticipant) => {
      set((state) => {
        const updateParticipant = (participants: RoomParticipant[] | undefined) => {
          // Enhanced defensive check: ensure participants array exists and initialize if needed
          if (!participants || !Array.isArray(participants)) {
            console.warn('⚠️ [RoomStore] updateParticipant: participants is undefined or invalid, initializing...');
            return [];
          }

          const existingParticipant = participants.find(p => p.id === participant.id);
          if (existingParticipant) {
            existingParticipant.isReady = participant.isReady;
            existingParticipant.agentConfiguration = participant.agentConfiguration;
          } else {
            // Add new participant if not found and has complete data
            if (participant.id && participant.username) {
              participants.push({
                id: participant.id,
                username: participant.username,
                isReady: participant.isReady || false,
                agentConfiguration: participant.agentConfiguration || null
              });
            } else {
              console.warn(`⚠️ [RoomStore] Incomplete participant data:`, participant);
            }
          }
          return participants;
        };

        const room = state.rooms.find(r => r.id === roomId);
        if (room) {
          room.participants = updateParticipant(room.participants);
        }

        if (state.currentRoom && state.currentRoom.id === roomId) {
          state.currentRoom.participants = updateParticipant(state.currentRoom.participants);
        }
      });
    },

    // P1.8: Handle game started event
    handleGameStarted: (data: { gameId: string; roomId: string }) => {
      set((state) => {
        state.gameStarted = true;
        state.gameId = data.gameId;
        console.log(`✅ Game started: gameId=${data.gameId}, roomId=${data.roomId}`);
      });
    },

    // P2.1: Handle spectator joined event
    handleSpectatorJoined: (data: { roomId: string; spectatorCount: number }) => {
      set((state) => {
        const room = state.rooms.find(r => r.id === data.roomId);
        if (room) {
          room.spectatorCount = data.spectatorCount;
        }

        if (state.currentRoom && state.currentRoom.id === data.roomId) {
          state.currentRoom.spectatorCount = data.spectatorCount;
        }
      });
    },

    // P2.1: Handle spectator left event
    handleSpectatorLeft: (data: { roomId: string; spectatorCount: number }) => {
      set((state) => {
        const room = state.rooms.find(r => r.id === data.roomId);
        if (room) {
          room.spectatorCount = data.spectatorCount;
        }

        if (state.currentRoom && state.currentRoom.id === data.roomId) {
          state.currentRoom.spectatorCount = data.spectatorCount;
        }
      });
    },

    // v3.2.139: Handle room state sync from backend
    handleRoomStateSynced: (data: { rooms: GameRoom[]; participants: RoomParticipant[] }) => {
      console.log('🔄 [RoomStore] Syncing room state:', data);
      set((state) => {
        // Update rooms list with latest backend data
        state.rooms = data.rooms || [];

        // If currently in a room, update currentRoom with latest data
        if (state.currentRoom) {
          const updatedRoom = data.rooms?.find(r => r.id === state.currentRoom!.id);
          if (updatedRoom) {
            state.currentRoom = updatedRoom;
          }
        }
      });
    },

    // Reset and cleanup
    resetRoomState: () => {
      set((state) => {
        state.rooms = [];
        state.roomsLoading = false;
        state.roomsError = null;
        state.currentRoom = null;
        state.isInRoom = false;
        state.myAgentConfiguration = null;
        state.isConfigured = false;
        state.createRoomLoading = false;
        state.createRoomError = null;
      });
    },

    clearErrors: () => {
      set((state) => {
        state.roomsError = null;
        state.createRoomError = null;
      });
    },

    // Agent creation flow control
    setAgentCreationStep: (step: number) => {
      set((state) => {
        state.agentCreationStep = Math.max(1, Math.min(3, step));
      });
    },

    nextAgentCreationStep: () => {
      set((state) => {
        state.agentCreationStep = Math.min(state.agentCreationStep + 1, 3);
      });
    },

    previousAgentCreationStep: () => {
      set((state) => {
        state.agentCreationStep = Math.max(state.agentCreationStep - 1, 1);
      });
    },

    // Slime selection
    setSlimeIndex: (index: number) => {
      set((state) => {
        state.currentSlimeIndex = index;
        if (state.myAgentConfiguration) {
          const slime = AVAILABLE_SLIMES[index];
          state.myAgentConfiguration.slimeType = slime.emoji;
          state.myAgentConfiguration.slimeName = slime.name;
        }
      });
    },

    nextSlime: () => {
      set((state) => {
        const newIndex = (state.currentSlimeIndex + 1) % AVAILABLE_SLIMES.length;
        state.currentSlimeIndex = newIndex;
        if (state.myAgentConfiguration) {
          const slime = AVAILABLE_SLIMES[newIndex];
          state.myAgentConfiguration.slimeType = slime.emoji;
          state.myAgentConfiguration.slimeName = slime.name;
        }
      });
    },

    previousSlime: () => {
      set((state) => {
        const newIndex = (state.currentSlimeIndex - 1 + AVAILABLE_SLIMES.length) % AVAILABLE_SLIMES.length;
        state.currentSlimeIndex = newIndex;
        if (state.myAgentConfiguration) {
          const slime = AVAILABLE_SLIMES[newIndex];
          state.myAgentConfiguration.slimeType = slime.emoji;
          state.myAgentConfiguration.slimeName = slime.name;
        }
      });
    },

    // Agent data updates
    updateAgentName: (name: string) => {
      set((state) => {
        // ✅ FIX: Auto-initialize if config is null (defense in depth)
        if (!state.myAgentConfiguration) {
          state.myAgentConfiguration = { ...initialAgentConfig };
        }
        state.myAgentConfiguration.name = name;
      });
    },

    updateAgentPersonality: (text: string) => {
      set((state) => {
        // ✅ FIX: Auto-initialize if config is null (defense in depth)
        if (!state.myAgentConfiguration) {
          state.myAgentConfiguration = { ...initialAgentConfig };
        }
        state.myAgentConfiguration.personality = text;
      });
    },

    updateAgentStrategy: (text: string) => {
      set((state) => {
        // ✅ FIX: Auto-initialize if config is null (defense in depth)
        if (!state.myAgentConfiguration) {
          state.myAgentConfiguration = { ...initialAgentConfig };
        }
        state.myAgentConfiguration.strategy = text;
      });
    },

    updateSelectedModel: (modelId: string) => {
      set((state) => {
        state.selectedModelId = modelId;
      });
    },

    // Timer management
    setAgentCreationTime: (secondsOrUpdater: number | ((prev: number) => number)) => {
      set((state) => {
        state.agentCreationTimeRemaining = typeof secondsOrUpdater === 'function'
          ? secondsOrUpdater(state.agentCreationTimeRemaining)
          : secondsOrUpdater;
      });
    },

    startAgentCreationTimer: () => {
      // Implemented via useEffect in component
      console.log('Timer started');
    },

    stopAgentCreationTimer: () => {
      // Implemented via useEffect in component
      console.log('Timer stopped');
    },

    // Agent data retrieval
    getCompleteAgentData: () => {
      return get().myAgentConfiguration;
    },

    getRemainingAttributePoints: () => {
      const config = get().myAgentConfiguration;
      if (!config) return 100;
      const total = config.hp + config.attack + config.defense + config.speed;
      return 100 - total;
    },

    // Reset agent creation
    resetAgentCreation: () => {
      set((state) => {
        state.agentCreationStep = 1;
        state.agentCreationTimeRemaining = 60;
        state.currentSlimeIndex = 0;
        state.myAgentConfiguration = { ...initialAgentConfig };
      });
    },

    // WaitingRoom control
    openWaitingRoom: () => {
      set((state) => {
        state.showWaitingRoom = true;
      });
    },

    closeWaitingRoom: () => {
      set((state) => {
        state.showWaitingRoom = false;
      });
    },

      toggleWaitingRoom: (show: boolean) => {
        set((state) => {
          state.showWaitingRoom = show;
        });
      },

    // Solo mode flag setter - CRITICAL FIX: Initialize config when entering solo mode
    setSinglePlayerMode: (flag: boolean) => {
      set((state) => {
        state.singlePlayerMode = !!flag;
        // ✅ FIX: Initialize myAgentConfiguration when entering solo mode
        if (flag && !state.myAgentConfiguration) {
          state.myAgentConfiguration = { ...initialAgentConfig };
        }
      });
    }
  })),
    {
      name: 'room-store', // localStorage key
      partialize: (state) => ({
        // Only persist critical state for page refresh recovery
        currentRoom: state.currentRoom,
        isInRoom: state.isInRoom,
        myAgentConfiguration: state.myAgentConfiguration,
        selectedModelId: state.selectedModelId,
        showWaitingRoom: state.showWaitingRoom,
        agentCreationStep: state.agentCreationStep,
        singlePlayerMode: state.singlePlayerMode,
      })
    }
  )
);
